import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { IntegrationsService } from '@services/integrations.service';
import { ScaffoldDialogRef } from '@app/framework/dialog/scaffold-dialog.service';
import { UserService } from '@services/user.service';
import { CourseService } from '@scaffold/services/course.service';
import { combineLatest } from 'rxjs';

@Component({
  selector: 'app-integration-select',
  templateUrl: './integration-select.component.html',
  styleUrls: ['./integration-select.component.scss'],
  standalone: false,
})
export class IntegrationSelectComponent implements OnInit {
  roleData: any;
  selectedIntegration: any;
  filterText: string = '';
  appendType: string = '';
  appendToCourse: string = '';
  filteredIntegrations: any[] = [];
  integrations: any[];
  course: any;
  loading: boolean = false;
  step: number = 1;
  appendTypeOptions = [
    { label: 'Prefix', value: 'prefix' },
    { label: 'Suffix', value: 'suffix' },
  ];
  constructor(
    private integrationsService: IntegrationsService,
    private dialogRef: ScaffoldDialogRef,
    private userService: UserService,
    private courseService: CourseService,
    private cd: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.userService.getRoleDetails().subscribe(data => {
      this.roleData = data;
    });
    this.loadIntegrations();
  }

  refreshIntegrations() {
    this.loadIntegrations();
  }

  private loadIntegrations() {
    this.loading = true;
    combineLatest([
      this.courseService.getCurrentCourseDetails(),
      this.integrationsService.getIntegrations(null, null, true),
    ]).subscribe(([course, integrations]) => {
      this.course = course;
      this.setIntegrations(integrations);
    });
  }

  filterIntegrations() {
    const filterText = this.filterText.toLowerCase();
    this.filteredIntegrations = this.integrations.filter(integration => {
      let filterName = integration.friendly_name || integration.name;
      return filterName?.toLowerCase().includes(filterText);
    });
  }

  selectIntegration(integration: any) {
    if (this.selectedIntegration === integration && this.step === 2) return;
    const integrationChanged = this.selectedIntegration !== integration;
    this.selectedIntegration = integration;
    if (integrationChanged) {
      this.applyAppendDefaults(integration);
    }
    this.step = 2;
    this.cd.detectChanges();
  }

  nextStep() {
    if (this.selectedIntegration) {
      this.step = 2;
      this.cd.detectChanges();
    }
  }

  prevStep() {
    this.step = 1;
    this.cd.detectChanges();
  }

  get courseTitle(): string {
    return this.course?.data?.title || 'Course Title';
  }

  get previewTitle(): string {
    const text = this.getNormalizedAppendText();
    if (!text) return this.courseTitle;
    return this.appendType === 'prefix'
      ? `${text}${this.courseTitle}`
      : `${this.courseTitle}${text}`;
  }

  get hasAppendPreview(): boolean {
    return !!(this.appendType && this.appendToCourse.trim());
  }

  getLogo(integration: any) {
    let type: string;
    if (integration.integration_type === 'dwh') {
      type = 'warehouse';
    } else if (integration.integration_type === 'live_event') {
      type = 'live_event';
    } else {
      type =
        integration.lms_type || integration.sis_type || integration.crm_type;
    }
    return `https://k16-hosted-lib.s3.us-east-2.amazonaws.com/${type}.png`;
  }

  getPlaceholderLogo() {
    return 'https://k16-hosted-lib.s3.us-east-2.amazonaws.com/placeholder.png';
  }

  onLogoError(event: Event) {
    (event.target as HTMLImageElement).src = this.getPlaceholderLogo();
  }

  getSyncExtra() {
    const appendToCourse = this.getNormalizedAppendText();
    if (!appendToCourse) return undefined;
    return {
      appendType: this.appendType,
      appendToCourse,
    };
  }

  hasIncompleteAppendSettings() {
    return !!(this.appendType || this.appendToCourse) && !this.getSyncExtra();
  }

  canSyncSelected() {
    return this.selectedIntegration && !this.hasIncompleteAppendSettings();
  }

  select(syncNewCourse: boolean) {
    this.dialogRef.close({
      selectedIntegration: this.selectedIntegration
        ? [this.selectedIntegration]
        : [],
      syncNewCourse,
      extra: this.getSyncExtra(),
    });
  }

  closeDialog() {
    this.dialogRef.close();
  }

  getNoIntegrationMessage() {
    if (this.roleData?.only_authorized_integrations) {
      return 'not_authorized_to_view_any_integrations';
    }
    return 'no_integrations_to_display';
  }

  private setIntegrations(integrations: any) {
    if (!integrations || integrations.length === 0 || integrations.errorType) {
      this.loading = false;
      this.integrations = [];
      this.filteredIntegrations = [];
      return;
    }

    this.integrations = integrations
      .map(integration => this.normalizeIntegration(integration))
      .map(integration => ({
        ...integration,
        has_course: this.courseHasIntegration(integration),
        is_source: this.getCourseData().source_lms === integration.name,
      }));
    this.filterIntegrations();
    this.loading = false;
  }

  private normalizeIntegration(integration: any): any {
    return {
      ...integration,
      ...(integration?.json_data || {}),
    };
  }

  private courseHasIntegration(integration: any): boolean {
    const courseData = this.getCourseData();
    const integrationName = integration?.name;
    if (!integrationName) return false;

    return (
      this.getDestinationLmsNames().includes(integrationName) ||
      Object.prototype.hasOwnProperty.call(courseData, `${integrationName}_id`)
    );
  }

  private getCourseData(): any {
    return this.course?.data || {};
  }

  private getDestinationLmsNames(): string[] {
    const destinationLms = this.getCourseData().destination_lms;
    if (Array.isArray(destinationLms)) return destinationLms;
    if (typeof destinationLms === 'string') {
      return destinationLms
        .split(/[|,]/)
        .map(name => name.trim())
        .filter(Boolean);
    }
    return [];
  }

  private applyAppendDefaults(integration: any) {
    const integrationData = integration?.json_data || integration || {};
    this.appendType = integrationData.appendType || '';
    this.appendToCourse = integrationData.appendToCourse || '';
  }

  private getNormalizedAppendText(): string | undefined {
    const text = this.appendToCourse.trim();
    if (!this.appendType || !text) return undefined;
    return this.appendType === 'prefix' ? `${text} ` : ` ${text}`;
  }
}
